// assets/tf-compare.js
(() => {
  const STORAGE_KEY = "tf_compare";

  // ===== store (wishlist style)
  const read = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const write = (handles) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    window.dispatchEvent(
      new CustomEvent("compare:changed", { detail: { handles } })
    );
  };

  const has = (handle) => read().includes(handle);

  const add = (handle) => {
    if (!handle) return;
    const list = read();
    if (!list.includes(handle)) list.unshift(handle);
    write(list);
  };

  const remove = (handle) => {
    if (!handle) return;
    write(read().filter((h) => h !== handle));
  };

  const toggle = (handle) => (has(handle) ? remove(handle) : add(handle));
  const clear = () => write([]);

  // ===== helpers
  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const money = (cents) => window.theme.formatMoney(cents);

  const fetchProduct = async (handle, signal) => {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      signal,
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error("fetch product fail");
    return res.json();
  };

  // ===== button
  class CompareButton extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;

      this.handle =
        this.dataset.handle || this.getAttribute("data-handle") || "";

      this.tooltipAdd = this.dataset.tooltipAdd || "Add To Compare";
      this.tooltipRemove = this.dataset.tooltipRemove || "Remove Compare";
      this.iconAdd = this.dataset.iconAdd || "icon-compare";
      this.iconRemove = this.dataset.iconRemove || "icon-trash";

      this.openOnAdd = String(this.dataset.openModalOnAdd || "true") === "true";

      this._onClick = this._onClick.bind(this);
      this._sync = this._sync.bind(this);

      this.$a = this.querySelector("a");
      this.$icon =
        this.querySelector("[data-compare-icon]") || this.querySelector(".icon");
      this.$tooltip =
        this.querySelector("[data-compare-tooltip]") ||
        this.querySelector(".tooltip");

      if (this.$a) this.$a.addEventListener("click", this._onClick);
      window.addEventListener("compare:changed", this._sync);

      this._sync();
    }

    disconnectedCallback() {
      if (this.$a) this.$a.removeEventListener("click", this._onClick);
      window.removeEventListener("compare:changed", this._sync);
    }

    _onClick(e) {
      e.preventDefault();
      if (!this.handle) return;

      const wasActive = has(this.handle);
      toggle(this.handle);

      if (this.openOnAdd && !wasActive) {
        window.dispatchEvent(new CustomEvent("compare:open"));
      }
    }

    _sync() {
      const active = has(this.handle);

      if (this.$icon) {
        this.$icon.classList.remove(this.iconAdd, this.iconRemove);
        this.$icon.classList.add(active ? this.iconRemove : this.iconAdd);
      }

      if (this.$tooltip) {
        this.$tooltip.textContent = active ? this.tooltipRemove : this.tooltipAdd;
      }

      this.classList.toggle("is-active", active);
      if (this.$a) this.$a.setAttribute("aria-pressed", String(active));
    }
  }

  // ===== overlay modal (NO <dialog>)
  class CompareModal extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;

      this.max = parseInt(this.dataset.max || "4", 10) || 4;
      this.compareUrl = this.dataset.compareUrl || "/pages/compare";

      // translate-ready
      this.titleText = this.dataset.title || "COMPARE PRODUCTS";
      this.emptyText = this.dataset.empty || "Your compare is curently empty";
      this.compareText = this.dataset.compare || "COMPARE";
      this.clearText = this.dataset.clear || "CLEAR ALL";

      this._abort = null;
      this._open = false;

      // IMPORTANT: prevent full rerender when we remove DOM item locally
      this._skipNextRerender = false;

      this._onOpen = this._onOpen.bind(this);
      this._onChanged = this._onChanged.bind(this);
      this._onKey = this._onKey.bind(this);

      this.renderShell();
      this.cache();

      window.addEventListener("compare:open", this._onOpen);
      window.addEventListener("compare:changed", this._onChanged);
      document.addEventListener("keydown", this._onKey);

      this.$close.addEventListener("click", () => this.close());
      this.$backdrop.addEventListener("click", () => this.close());
      this.$clear.addEventListener("click", (e) => {
        e.preventDefault();
        clear();
      });

      // delegate remove item (remove 1 item only, no full reload)
      this.$panel.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-compare-remove]");
        if (!btn) return;
        e.preventDefault();

        const handle = btn.getAttribute("data-handle");

        // 1) remove DOM item instantly (no fetch)
        const item = btn.closest("[data-compare-item]");
        if (item) item.remove();

        // 2) update empty/count instantly
        this.syncEmptyState();
        this.updateCount();

        // 3) update storage but skip rerender once
        this._skipNextRerender = true;
        remove(handle);
      });

      this.updateCount();
      // don't renderList until open (avoid initial fetch)
      this.close(true);
    }

    disconnectedCallback() {
      window.removeEventListener("compare:open", this._onOpen);
      window.removeEventListener("compare:changed", this._onChanged);
      document.removeEventListener("keydown", this._onKey);
      if (this._abort) this._abort.abort();
    }

    renderShell() {
      this.innerHTML = `
        <div class="tf-compare-overlay" data-compare-overlay>
          <div class="tf-compare-backdrop" data-compare-backdrop></div>

          <div class="tf-compare-panel" role="dialog" aria-modal="true" aria-hidden="true" data-compare-panel>
            <button class="tf-compare-close" type="button" aria-label="Close" data-compare-close>&times;</button>

            <div class="tf-compare-head">
              <h2 class="tf-compare-title">${esc(this.titleText)}</h2>
            </div>

            <div class="modal-body main-list-clear">
              <div class="tf-compare-inner">
                <div class="tf-compare-list list-empty" data-compare-list>
                  <p class="text-empty" data-compare-empty style="display:none;">${esc(
        this.emptyText
      )}</p>
                </div>
              </div>

              <div class="group-btn">
                <a href="${esc(this.compareUrl)}" class="tf-btn  fw-medium" data-compare-go>
                  ${esc(this.compareText)} <span class="count-item-compare" data-compare-count>(0)</span>
                </a>

                <button type="button" class="tf-btn fw-medium clear-list-empty" data-compare-clear>
                  <span>${esc(this.clearText)}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    cache() {
      this.$overlay = this.querySelector("[data-compare-overlay]");
      this.$backdrop = this.querySelector("[data-compare-backdrop]");
      this.$panel = this.querySelector("[data-compare-panel]");
      this.$close = this.querySelector("[data-compare-close]");
      this.$list = this.querySelector("[data-compare-list]");
      this.$empty = this.querySelector("[data-compare-empty]");
      this.$count = this.querySelector("[data-compare-count]");
      this.$clear = this.querySelector("[data-compare-clear]");
    }

    _onOpen() {
      this.open();
    }

    _onChanged() {
      this.updateCount();

      // if this change was triggered by our own "remove DOM item" -> skip rerender
      if (this._skipNextRerender) {
        this._skipNextRerender = false;
        return;
      }

      if (this._open) this.renderList();
    }

    _onKey(e) {
      if (!this._open) return;
      if (e.key === "Escape") this.close();
    }

    open() {
      this._open = true;
      this.$overlay.classList.add("is-open");
      this.$panel.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("tf-compare-lock");
      this.renderList();
    }

    close(silent = false) {
      this._open = false;
      this.$overlay.classList.remove("is-open");
      this.$panel.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("tf-compare-lock");
      if (!silent) window.dispatchEvent(new CustomEvent("compare:closed"));
    }

    updateCount() {
      const n = read().length;
      if (this.$count) this.$count.textContent = `(${n})`;
      document.querySelectorAll("[data-compare-badge]").forEach((el) => {
        el.textContent = String(n);
      });
    }

    syncEmptyState() {
      const left = this.$list.querySelectorAll("[data-compare-item]").length;
      this.$empty.style.display = left <= 0 ? "" : "none";
    }

    async renderList() {
      const handles = read().slice(0, this.max);

      // clear existing items (then build)
      this.$list.querySelectorAll("[data-compare-item]").forEach((n) => n.remove());

      if (!handles.length) {
        this.$empty.style.display = "";
        return;
      }
      this.$empty.style.display = "none";

      if (this._abort) this._abort.abort();
      this._abort = new AbortController();

      const results = await Promise.allSettled(
        handles.map((h) => fetchProduct(h, this._abort.signal))
      );

      const products = results
        .map((r) => (r.status === "fulfilled" ? r.value : null))
        .filter(Boolean);

      const html = products
        .map((p) => {
          const handle = (p.handle || "").trim();
          const img1 = p.featured_image || p.images?.[0] || "";
          const img2 = p.images?.[1] || img1;

          const price = typeof p.price === "number" ? p.price : 0;
          const compareAt =
            typeof p.compare_at_price === "number" ? p.compare_at_price : 0;

          return `
            <div class="tf-compare-item card_product--V01 file-delete" data-compare-item>
              <div class="card_product-wrapper aspect-ratio-1">
                <span class="remove icon-close" data-compare-remove data-handle="${esc(handle)}"></span>
                <a href="${esc(p.url)}" class="product-img">
                  <img src="${esc(img1)}" alt="${esc(p.title)}" class="img-product" loading="lazy">
                  <img src="${esc(img2)}" alt="${esc(p.title)}" class="img-hover" loading="lazy">
                </a>
              </div>
              <div class="card_product-info text-center">
                <a href="${esc(p.url)}" class="name-product link text-line-clamp-2">
                  ${esc(p.title)}
                </a>
                <div class="price-wrap justify-content-center">
                  <span class="price-new">${esc(money(price))}</span>
                  ${compareAt > price
              ? `<span class="price-old fw-normal text-caption">${esc(
                money(compareAt)
              )}</span>`
              : ""
            }
                </div>
              </div>
            </div>
          `;
        })
        .join("");

      this.$list.insertAdjacentHTML("beforeend", html);
      this.syncEmptyState();
    }
  }

  customElements.define("compare-button", CompareButton);
  customElements.define("compare-modal", CompareModal);

  window.TFCompare = { read, write, has, add, remove, toggle, clear };
})();
